import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { X, Trash2, SkipForward, Music } from 'lucide-react';
import { toast } from 'sonner';

// Sky Club YouTube cue — Randall & staff add songs, everyone in the club hears them.
export const SkyClubCue = ({ authFetch, API_URL, canManage }) => {
  const [open, setOpen] = useState(false);
  const [queue, setQueue] = useState([]);
  const [idx, setIdx] = useState(0);
  const [url, setUrl] = useState('');
  const iframeRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${API_URL}/api/mansion/thegods/queue`);
      const d = await r.json();
      setQueue(d.queue || []);
    } catch (e) { /* noop */ }
  }, [API_URL]);

  useEffect(() => {
    load();
    const iv = setInterval(load, 20000);
    return () => clearInterval(iv);
  }, [load]);

  // auto-advance when the current video ends (YouTube iframe API postMessage)
  useEffect(() => {
    const onMsg = (ev) => {
      if (!String(ev.origin).includes('youtube.com')) return;
      try {
        const d = JSON.parse(ev.data);
        if (d?.event === 'onStateChange' && d?.info === 0) setIdx((i) => i + 1);
      } catch (e) { /* noop */ }
    };
    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
  }, []);

  // ask the iframe to send us events
  const onIframeLoad = () => {
    try {
      iframeRef.current?.contentWindow?.postMessage(JSON.stringify({ event: 'listening', id: 'skyclub' }), '*');
    } catch (e) { /* noop */ }
  };

  const add = async () => {
    const u = url.trim();
    if (!u) return;
    try {
      const r = await authFetch(`${API_URL}/api/mansion/thegods/queue`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ url: u }),
      });
      const d = await r?.json();
      if (r?.ok) { setQueue(d.queue || []); setUrl(''); toast.success(d.message); }
      else toast.error(d?.detail || 'Failed');
    } catch (e) { toast.error('Failed'); }
  };

  const remove = async (qid) => {
    try {
      const r = await authFetch(`${API_URL}/api/mansion/thegods/queue/${qid}`, { method: 'DELETE' });
      const d = await r?.json();
      if (r?.ok) setQueue(d.queue || []);
    } catch (e) { /* noop */ }
  };

  const current = queue.length ? queue[idx % queue.length] : null;

  return (
    <>
      <div className="absolute bottom-2 right-2">
        <Button size="sm" className="h-8 text-xs bg-black/70 text-fuchsia-300 border border-fuchsia-500/40" onClick={() => setOpen(!open)} data-testid="skyclub-cue-btn">
          <Music className="w-3 h-3 mr-1" /> YouTube Cue {queue.length ? `(${queue.length})` : ''}
        </Button>
      </div>
      {open && (
        <div className="absolute right-2 bottom-12 w-72 bg-gray-900/95 border border-fuchsia-500/50 rounded-xl p-3 z-20" data-testid="skyclub-cue-panel">
          <div className="flex justify-between items-center mb-2">
            <h4 className="text-white text-sm font-bold">🎵 Sky Club Cue</h4>
            <button onClick={() => setOpen(false)}><X className="w-4 h-4 text-white/60" /></button>
          </div>
          {current ? (
            <div className="rounded-lg overflow-hidden border border-fuchsia-500/30 mb-2">
              <iframe
                ref={iframeRef}
                key={`${current.qid}_${idx}`}
                title="Sky Club Cue"
                src={`https://www.youtube.com/embed/${current.video_id}?autoplay=1&enablejsapi=1&playsinline=1`}
                className="w-full aspect-video"
                allow="autoplay; encrypted-media"
                onLoad={onIframeLoad}
                data-testid="skyclub-cue-player"
              />
              <div className="flex items-center justify-between bg-black/60 px-2 py-1">
                <span className="text-white/60 text-[10px]">by {current.added_by}</span>
                <button onClick={() => setIdx((i) => i + 1)} className="text-fuchsia-300 text-xs flex items-center gap-1" data-testid="skyclub-cue-next">
                  <SkipForward className="w-3 h-3" /> Next
                </button>
              </div>
            </div>
          ) : (
            <p className="text-white/40 text-xs text-center py-3">The cue is empty — Randall, drop some heat 🔥</p>
          )}
          {canManage && (
            <div className="flex gap-1.5 mb-2">
              <Input placeholder="YouTube link…" value={url} onChange={(e) => setUrl(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && add()} className="h-8 text-xs" data-testid="skyclub-cue-input" />
              <Button size="sm" className="h-8 bg-fuchsia-600 hover:bg-fuchsia-500" onClick={add} data-testid="skyclub-cue-add-btn">Add</Button>
            </div>
          )}
          <div className="max-h-32 overflow-y-auto space-y-1">
            {queue.map((q, i) => (
              <div key={q.qid} className={`flex items-center justify-between rounded px-2 py-1 ${i === idx % (queue.length || 1) ? 'bg-fuchsia-500/20' : 'bg-white/5'}`}>
                <button onClick={() => setIdx(i)} className="text-white/75 text-[11px] truncate text-left flex-1">
                  {i + 1}. {q.video_id} <span className="text-white/35">· {q.added_by}</span>
                </button>
                {canManage && (
                  <button onClick={() => remove(q.qid)} className="text-red-400/70 hover:text-red-300 ml-1" data-testid={`skyclub-cue-remove-${q.qid}`}>
                    <Trash2 className="w-3 h-3" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
};
